import { type FC, useEffect, useState } from "react";
import { Text } from "@/components/podkit/typography/Text";
import { cn } from "@/components/podkit/lib/cn";
import { EnvironmentStatus_DevContainer_Presence } from "gitpod-next-api/gitpod/v1/environment_pb";
import type { PlainEnvironment } from "@/queries/environment-queries";
import IconLightbulbGlow from "central-icons/IconLightbulbGlow";
import { withGitpodSize } from "@/assets/icons/utils";

const LightbulbIcon = withGitpodSize(IconLightbulbGlow);

// Animation timing constants
// 8 seconds chosen to give users time to read tips without feeling rushed
// while keeping the experience dynamic
const ANIMATION_CYCLE_MS = 8000;
const CONTENT_FADE_DURATION_MS = 500;
const CONTENT_FADE_START_MS = ANIMATION_CYCLE_MS - CONTENT_FADE_DURATION_MS; // 7500ms

const ONA_TIPS = [
    "Ona starts a development environment per task, ensuring a clean and isolated workspace.",
    "Development environments are based on the devcontainer format and get built automatically.",
    "Ask Ona to run tests, check code quality, or debug issues automatically.",
    "Ona maintains context throughout your session - no need to repeat information.",
    "Ona will make a plan for your task, breaking it down into manageable steps.",
    "You can stop Ona at any time, or give it guidance while it's working.",
    "Ask Ona to explain complex code patterns or suggest improvements.",
    "Place an AGENTS.md file in your project root to provide Ona with guidelines and context.",
    "Use Ona for code reviews, refactoring, and architectural guidance.",
    "Ona can help with documentation, API design, and testing strategies.",
];

const DEV_CONTAINER_TIP =
    "We picked up your dev container configuration. Ona caches dev container builds - starts become faster over time.";

export const OnaTips: FC<{ className?: string; environment?: PlainEnvironment }> = ({ className, environment }) => {
    // Check if we should show the dev container tip
    const hasCustomDevContainer =
        environment?.status?.devcontainer?.devcontainerFilePresence ===
            EnvironmentStatus_DevContainer_Presence.DISCOVERED ||
        environment?.status?.devcontainer?.devcontainerFilePresence ===
            EnvironmentStatus_DevContainer_Presence.SPECIFIED;

    // Use dev container tip if applicable, otherwise use regular tips
    const tips = hasCustomDevContainer ? [DEV_CONTAINER_TIP, ...ONA_TIPS] : ONA_TIPS;
    const [currentTipIndex, setCurrentTipIndex] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [hasResetForDevContainer, setHasResetForDevContainer] = useState(false);

    // Reset to show dev container tip immediately when hasCustomDevContainer becomes true (only once)
    useEffect(() => {
        if (hasCustomDevContainer && !hasResetForDevContainer) {
            setCurrentTipIndex(0);
            setHasResetForDevContainer(true);
        }
    }, [hasCustomDevContainer, hasResetForDevContainer]);

    useEffect(() => {
        // Synchronize with aura animation cycle
        // Both aura and content should cycle together
        let fadeOutTimeoutId: ReturnType<typeof setTimeout>;
        let swapTimeoutId: ReturnType<typeof setTimeout>;
        let cycleTimeoutId: ReturnType<typeof setTimeout>;

        const scheduleNextCycle = () => {
            // Start fading out content before cycle ends
            fadeOutTimeoutId = setTimeout(() => {
                setIsTransitioning(true);
            }, CONTENT_FADE_START_MS);

            // Swap content and fade in (synchronized with aura cycle reset)
            swapTimeoutId = setTimeout(() => {
                setCurrentTipIndex((prev) => (prev + 1) % tips.length);
                setIsTransitioning(false);
            }, ANIMATION_CYCLE_MS);

            // Schedule next cycle
            cycleTimeoutId = setTimeout(() => {
                scheduleNextCycle();
            }, ANIMATION_CYCLE_MS);
        };

        scheduleNextCycle();

        return () => {
            clearTimeout(fadeOutTimeoutId);
            clearTimeout(swapTimeoutId);
            clearTimeout(cycleTimeoutId);
        };
    }, [tips.length]); // Only recreate when tips array changes, not on every tip index change

    return (
        <div className={cn("w-full", className)}>
            <div className="flex animate-ripple-aura items-start gap-3 rounded-xl border border-border-subtle bg-surface-secondary p-4 shadow-sm motion-reduce:animate-none">
                <span className="flex size-6 flex-shrink-0 items-center justify-center rounded-lg bg-[rgb(var(--ona-blue-100))] text-[rgb(var(--surface-brand-accent-06))]">
                    <LightbulbIcon size="base" className="size-4" />
                </span>
                <div className="min-w-0 flex-1">
                    <Text className="text-base font-medium text-content-brand">Did you know?</Text>
                    <div className="relative">
                        <Text
                            className={cn(
                                "duration-[500ms] text-base text-content-primary transition-opacity ease-in-out",
                                !isTransitioning ? "opacity-100" : "opacity-0",
                            )}
                        >
                            {tips[currentTipIndex]}
                        </Text>
                    </div>
                </div>
            </div>
        </div>
    );
};
